import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import {
  SnackbarApiErrorMessage,
  useDataProvider,
  useMasterData,
  useLocalization,
  useDialog,
  useSnackbar,
} from '@ui-framework/index';
import { nanoid } from 'nanoid';
import IconLibrary from '../IconLibrary';
import DialogCloseButton from './DialogCloseButton';

function CancelJobDialog({ open, onClose, jobId }) {
  const { formatMessage } = useLocalization();
  const snackbar = useSnackbar();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { getApiUrl, post, get } = useDataProvider(provider);

  const [loading, setLoading] = useState(false);
  const [serviceJob, setServiceJob] = useState(null);

  const { cancellableJobStatuses: validStatuses } = useMasterData();
  const { confirmationRef } = useDialog();

  const fetchServiceJob = async () => {
    try {
      const url = `/sxcl/v1/service-jobs/${jobId}`;
      const response = await get(url);
      if (response?.ok) {
        return response.data;
      }
      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={response?.errors} />,
      });
    } catch (err) {
      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={[{ message: err.message }]} />,
      });
    }
    return null;
  };

  async function cancelJob() {
    setLoading(true);
    if (validStatuses.includes(serviceJob?.serviceJobStatus)) {
      const body = { recordVersion: serviceJob?.recordVersion };
      const response = await post(
        `${getApiUrl()}/sxcl/v1/service-jobs/${jobId}/operations/cancel-service-job`,
        body,
      );
      if (response.ok) {
        confirmationRef.current.init({
          message: 'Job was canceled successfully',
          positiveButtonText: 'Close',
          width: 360,
          title: 'Job Canceled',
          onChange: () => {
            confirmationRef.current.setOpen(false);
            onClose(nanoid());
          },
        });
        confirmationRef.current.setOpen(true);
      } else {
        snackbar.showMessage({
          type: 'error',
          data: <SnackbarApiErrorMessage errors={response?.errors} />,
        });
      }
    } else {
      snackbar.showMessage({
        type: 'error',
        data: formatMessage({
          id: 'CANCEL_JOB_STATUS_FAILURE',
          defaultMessage: 'Error: Could not cancel the job in this status',
        }),
      });
    }
    setLoading(false);
  }

  const buttons = [
    {
      label: formatMessage({ id: 'CANCEL_JOB', defaultMessage: 'Cancel Job' }),
      onClick: () => cancelJob(),
      variant: 'contained',
      color: 'primary',
      disabled: !validStatuses.includes(serviceJob?.serviceJobStatus),
      tooltip: 'Cannot cancel job in this status',
      startIcon: <IconLibrary.CancelJobAction />,
    },
    {
      label: formatMessage({
        id: 'DONT_CANCEL_JOB',
        defaultMessage: "Don't Cancel Job",
      }),
      onClick: () => {
        onClose(null);
      },
      variant: 'contained',
      color: 'secondary',
      startIcon: <IconLibrary.Yes />,
    },
  ];

  const initialize = async () => {
    setLoading(true);
    const sj = await fetchServiceJob();
    setServiceJob(sj);
    setLoading(false);
  };

  useEffect(() => {
    if (open) {
      initialize();
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        onClose(null);
      }}
      maxWidth="sm"
      fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconLibrary.CancelServiceJob />
        {formatMessage({
          id: 'CANCEL_JOB',
          defaultMessage: 'Cancel Job',
        })}
        &nbsp;&ndash;
        <Typography color="primary" variant="body">
          {serviceJob?.serviceJobNumber}
        </Typography>
        <DialogCloseButton
          disabled={loading}
          onClose={() => {
            onClose(null);
          }}
        />
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box textAlign="center">
            <CircularProgress size="30px" />
            <Typography>Processing</Typography>
          </Box>
        ) : (
          `Are you sure you want to Cancel Job ${serviceJob?.serviceJobNumber}?`
        )}
      </DialogContent>
      <DialogActions display="flex" sx={{ justifyContent: 'center', my: 2 }}>
        {buttons.map(btn => {
          const { label, tooltip, ...rest } = btn;
          return (
            <Tooltip
              key={label}
              title={
                validStatuses.includes(serviceJob?.serviceJobStatus)
                  ? ''
                  : tooltip
              }>
              <span>
                <Button key={label} disabled={loading} {...rest}>
                  {label}
                </Button>
              </span>
            </Tooltip>
          );
        })}
      </DialogActions>
    </Dialog>
  );
}

export default CancelJobDialog;
